import { DOCUMENT } from "@angular/common"
import { Injectable, inject } from "@angular/core"

export type SchemaType = "WebPage" | "NewsMediaOrganization" | "ItemList" | "SiteNavigationElement"

@Injectable({
  providedIn: "root",
})
export class StructuredDataService {
  private document = inject(DOCUMENT)
  private scriptElements: Map<string, HTMLScriptElement> = new Map()

  /**
   * Add schema.org JSON-LD structured data to the document head
   * @param type Schema type identifier
   * @param data The JSON-LD data object
   */
  addSchema(type: SchemaType | string, data: any): void {
    // Remove existing schema of this type if it exists
    this.removeSchema(type)

    // Create new script element
    const script = this.document.createElement("script")
    script.type = "application/ld+json"
    script.text = JSON.stringify(data)

    // Add a custom attribute to identify this script later
    script.setAttribute("data-schema-type", type)

    // Add to document head
    this.document.head.appendChild(script)

    // Store reference to the script element
    this.scriptElements.set(type, script)
  }

  /**
   * Remove schema.org JSON-LD structured data from the document head
   * @param type Schema type identifier
   */
  removeSchema(type: SchemaType | string): void {
    // Check if we have a reference to this script
    const existingScript = this.scriptElements.get(type)
    if (existingScript) {
      this.document.head.removeChild(existingScript)
      this.scriptElements.delete(type)
      return
    }

    // Fallback: search for script by data attribute
    const scripts = this.document.head.querySelectorAll(`script[data-schema-type="${type}"]`)
    scripts.forEach((script) => {
      this.document.head.removeChild(script)
    })
  }

  /**
   * Clear all schema.org JSON-LD structured data added by this service
   */
  clearAllSchemas(): void {
    this.scriptElements.forEach((script, type) => {
      this.removeSchema(type)
    })
  }
}
